import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, PenLine, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { pulizieApi, erroreApi, meseCorrente, dataBreve } from './api';
import { FirmaCanvas } from './FirmaCanvas';

const descrivi = (r) => {
  if (r.tipo === 'allergeni') return `Dopo allergeni${r.allergeni?.length ? `: ${r.allergeni.join(', ')}` : ''}`;
  if (r.esito === 'problema') return `${r.zona}: ${r.problema} → ${r.azione}`;
  return `${r.zona} (${r.attivita?.length || 0} attività)`;
};

const perGiorno = (regs) => regs.reduce((acc, r) => ({ ...acc, [r.data]: [...(acc[r.data] || []), r] }), {});

const Firma = ({ mese, onFirmato }) => {
  const [firma, setFirma] = useState(null);
  const [busy, setBusy] = useState(false);
  const salva = async () => {
    setBusy(true);
    try {
      await pulizieApi.post('/verifica', { mese, firma });
      toast.success('Registro firmato');
      onFirmato();
    } catch (e) {
      toast.error(erroreApi(e, 'Firma non salvata'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="pulizie-firma-box">
      <p className="text-sm font-medium flex items-center gap-2"><PenLine className="h-4 w-4" />Firma del responsabile</p>
      <FirmaCanvas onChange={setFirma} />
      <Button onClick={salva} disabled={!firma || busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
        data-testid="pulizie-firma-salva">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ho controllato il registro: firmo'}
      </Button>
    </div>
  );
};

export const RegistroDialog = ({ open, onClose }) => {
  const [mese, setMese] = useState(meseCorrente());
  const [dati, setDati] = useState(null);
  const [scarico, setScarico] = useState(false);

  const carica = useCallback(async () => {
    try { setDati(await pulizieApi.get(`/registro?mese=${mese}`)); } catch (e) { toast.error(erroreApi(e, 'Registro non disponibile')); }
  }, [mese]);
  useEffect(() => { if (open) carica(); }, [open, carica]);

  const pdf = async () => {
    setScarico(true);
    try {
      const blob = await pulizieApi.get(`/report-pdf/${mese}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: `Registro_Pulizie_${mese}.pdf` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF non disponibile');
    } finally {
      setScarico(false);
    }
  };

  const giorni = perGiorno(dati?.registrazioni || []);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="pulizie-registro-dialog">
        <DialogHeader>
          <DialogTitle>Registro del mese</DialogTitle>
          <DialogDescription>Il PDF contiene registro, problemi, piano e detergenti: è quello da mostrare all'ASL.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <input type="month" value={mese} max={meseCorrente()} onChange={(e) => setMese(e.target.value)}
            className="flex-1 h-11 px-3 rounded-lg border border-gray-300" data-testid="pulizie-registro-mese" />
          <Button onClick={pdf} disabled={scarico} className="h-11 bg-blue-900 hover:bg-blue-800 text-white" data-testid="pulizie-registro-pdf">
            {scarico ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" />PDF</>}
          </Button>
        </div>
        <div className="space-y-3" data-testid="pulizie-registro-lista">
          {!dati ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
            : !dati.registrazioni.length ? <p className="text-sm text-gray-500 text-center py-4">Nessuna registrazione in questo mese</p>
              : Object.entries(giorni).map(([g, regs]) => (
                <div key={g}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{dataBreve(g)}</p>
                  {regs.map((r) => (
                    <p key={r.id} className={`text-sm ${r.esito === 'problema' ? 'text-amber-700' : 'text-gray-800'}`}>
                      <span className="text-gray-500">{r.ora}</span> · {descrivi(r)} · <span className="text-gray-500">{r.operatore}</span>
                      {r.inserita_da && r.inserita_da !== r.operatore && r.compilazione !== 'periodo' && (
                        <span className="text-xs text-gray-400"> (inserita da {r.inserita_da})</span>
                      )}
                    </p>
                  ))}
                </div>
              ))}
        </div>
        {dati?.verifica ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="pulizie-registro-firmato">
            <CheckCircle2 className="h-4 w-4" />Controllato e firmato da {dati.verifica.responsabile} il {dati.verifica.data}
          </p>
        ) : dati?.puo_firmare && dati.registrazioni.length > 0 && <Firma mese={mese} onFirmato={carica} />}
      </DialogContent>
    </Dialog>
  );
};
